import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api.js';
import { t, geleden } from '../i18n.js';
import { Ernst } from '../components/Status.jsx';

/**
 * Alerts, zoals in het prototype: de open alerts bovenaan, op ernst; wat weer goed is (hersteld)
 * daaronder, want dat wacht alleen nog op iemand die het afsluit. Filteren op open, afgesloten of alle; zoeken gaat
 * altijd door alle alerts (de server zoekt, ook in waarden als een adres of gebruiker, en in notities).
 */
export default function Alerts({ klant }) {
  /*
   * De filters staan in de URL, zodat een tegel op het overzicht hierheen linkt met de filters erop (status, module, de
   * laatste zoveel dagen, afgesloten door ZERO of een medewerker), en terug in de browser de vorige stand geeft.
   */
  const [zoek, zetZoek] = useSearchParams();
  const zet = (sleutel, waarde) => zetZoek((oud) => {
    const n = new URLSearchParams(oud);
    if (waarde) n.set(sleutel, waarde); else n.delete(sleutel);
    return n;
  }, { replace: true });
  const status = ['afgesloten', 'alle'].includes(zoek.get('status')) ? zoek.get('status') : 'open';
  const module = zoek.get('module');
  const dagen = /^\d{1,3}$/.test(zoek.get('dagen') ?? '') ? Number(zoek.get('dagen')) : null;
  const door = ['zero', 'medewerker'].includes(zoek.get('door')) ? zoek.get('door') : null;
  const [tekst, zetTekst] = useState(zoek.get('q') ?? '');
  const gezocht = zoek.get('q') ?? '';
  useEffect(() => { const k = setTimeout(() => zet('q', tekst.trim()), 300); return () => clearTimeout(k); }, [tekst]);
  const q = useQuery({ queryKey: ['alerts', klant, gezocht ? 'zoek' : status, gezocht, dagen, door],
    queryFn: () => api.alerts(klant, gezocht ? 'alle' : status === 'open' ? undefined : status, gezocht || undefined, { dagen, door }),
    /* Vorige data vasthouden bij een filter-/periodewissel, maar niet bij een klantwissel (klant op index 1). */
    refetchInterval: 15_000, placeholderData: (vorige, q) => (q?.queryKey?.[1] === klant ? vorige : undefined) });
  const chip = (stand, label) => (
    <button type="button" className="chip" aria-pressed={!gezocht && status === stand} disabled={Boolean(gezocht)}
      onClick={() => zet('status', stand === 'open' ? null : stand)}>{label}</button>
  );
  const weg = (sleutel, label) => <button type="button" className="chip" aria-pressed="true" onClick={() => zet(sleutel, null)}>{label} ✕</button>;
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Alerts')}</h1>
        <p className="sub">{t('Eén alert per voorval. Afsluiten doet een medewerker, met een reden.')}</p>
      </div></div>
      <div className="filters">
        {chip('open', t('Open'))}
        {chip('afgesloten', t('Afgesloten'))}
        {chip('alle', t('Alle'))}
        <input type="search" className="search" value={tekst} onChange={(e) => zetTekst(e.target.value)} maxLength={100}
          style={{ minWidth: 'min(340px, 100%)' }} placeholder={t('Zoek in alle alerts: nummer, adres, gebruiker, klant…')} aria-label={t('Zoek in alle alerts')} />
        {module && weg('module', t(q.data?.data.alerts.find((a) => a.module?.id === module)?.module.label ?? module))}
        {dagen && weg('dagen', dagen === 1 ? t('ontstaan in de laatste 24 uur') : t('ontstaan in de laatste {n} dagen', { n: dagen }))}
        {door && weg('door', door === 'zero' ? t('afgesloten door ADTS ZERO') : t('afgesloten door een medewerker'))}
      </div>
      <MeldingStoring stand={q.data?.data.notifications} />
      <section className="card">
        {q.isPending ? <p className="muted">{t('Laden…')}</p>
          : q.isError ? <div className="melder rood">{t(q.error.message)}</div>
          : <AlertRijen alerts={module ? q.data.data.alerts.filter((a) => a.module?.id === module) : q.data.data.alerts} toonKlant={klant === 'alle'}
              leeg={gezocht ? t('Geen alerts gevonden met "{q}".', { q: gezocht }) : status === 'afgesloten' ? t('Geen afgesloten alerts.') : status === 'alle' ? t('Geen alerts.') : undefined} />}
        {gezocht && <p className="noot">{t('Gezocht in alle alerts, ook afgesloten. Hoogstens 200.')}</p>}
      </section>
    </>
  );
}

/** De rijen van een lijst alerts. `basis` gaat voor de link (bij meekijken: /meekijken/klant). */
export function AlertRijen({ alerts, toonKlant, basis = '', leeg }) {
  if (!alerts.length) return <p className="empty">{leeg ?? t('Geen open alerts.')}</p>;
  return (
    <div className="rows">
      {alerts.map((a) => (
        <Link key={a.number} className="row" to={`${basis}/alerts/${a.number}`}
          style={{ gridTemplateColumns: '92px minmax(0,1fr) auto auto' }}>
          <Ernst ernst={a.severity} />
          <span><span className="title">{t(a.title.key, a.title.params)}</span>{toonKlant && <> <span className="ttag">{a.tenant.name}</span></>}
            <br /><span className="muted" style={{ fontSize: 12 }}><span className="mono">{a.number}</span>{a.module && <> · {t(a.module.label)}</>}</span></span>
          <span className={`pill ${a.status === 'in behandeling' ? 'behandeling' : a.status}`}>
            {a.resolvedAt && a.status !== 'afgesloten' ? t('hersteld') : t(a.status)}
          </span>
          <span className="muted">{geleden(a.lastAt)}</span>
        </Link>
      ))}
    </div>
  );
}

/** Een melding die stilletjes niet uitgaat, is erger dan geen melding (besluit meldingen). */
export function MeldingStoring({ stand }) {
  if (!stand || (!stand.mislukt && !stand.opnieuw && !stand.uit)) return null;
  return (
    <div className="callout crit" style={{ marginBottom: 14 }}>
      {stand.mislukt > 0 && <>{t('{n} melding(en) zijn niet verstuurd.', { n: stand.mislukt })} </>}
      {stand.opnieuw > 0 && <>{t('{n} melding(en) worden opnieuw geprobeerd.', { n: stand.opnieuw })} </>}
      {stand.uit > 0 && <>{t('{n} melding(en) gingen niet weg omdat een kanaal niet is ingesteld.', { n: stand.uit })} </>}
      {t('Kijk onder Beheer › Meldingen.')}
    </div>
  );
}
